<script>
  import { onMount, onDestroy } from "svelte";
  import {
    Page,
    Navbar,
    NavRight,
    Link,
    Card,
    CardHeader,
    CardContent,
    Row,
    Col,
    Button,
    Block,
    Toggle,
    Range,
    Icon,
    Badge,
    Popup,
    f7,
    f7ready,
  } from "framework7-svelte";
  import SoundAlertManager from "../utils/soundAlerts";
  import { handleUnauthorized } from "../js/auth";
  import { ENGINE_URL, BASE_URL, ENABLE_SIMULATION } from "../services/service.config";
  import { fetchUsersDirectory } from "../services/userService";
  import { TradingService } from "../services/tradingService";
  import { formatCapital, isTierUnassigned, formatTierBadge } from "../utils/formatters";
  import UserSearchTypeahead from "../components/UserSearchTypeahead.svelte";
  import AssignUccTagModal from "../components/AssignUccTagModal.svelte";
  import AuditClientTimeline from "../components/AuditClientTimeline.svelte";
  import VirtualBalanceModal from "../components/VirtualBalanceModal.svelte";
  import LiveSignalChartModal from "../components/LiveSignalChartModal.svelte";
  import EnvironmentSwitcher from "../components/EnvironmentSwitcher.svelte";
  import TradingNavbar from "../components/TradingNavbar.svelte";

  export let f7route = undefined;
  export let f7router = undefined;

  // Active Environment: Pure Live Desk
  let currentEnv = "LIVE";

  let showChartModal = false;
  let chartSymbol = "";
  let chartScripCode = "";

  function openStockChart(stockName, bseCode) {
    chartSymbol = stockName;
    chartScripCode = String(bseCode || "");
    showChartModal = true;
  }

  let requestGeneration = 0;
  let activeAbortController = null;

  // RBAC Permission Check
  let userRoles = [];
  try {
    userRoles = JSON.parse(localStorage.getItem("user_roles") || "[]");
  } catch (e) {
    userRoles = [];
  }
  let canAccessLive =
    userRoles.includes("super_admin") ||
    userRoles.includes("admin") ||
    userRoles.includes("dealer_lead") ||
    userRoles.length === 0; // Default permit in dev/staging

  let ucc = (f7route && f7route.query && f7route.query.ucc ? f7route.query.ucc : "").trim().toUpperCase();
  let searchInput = "";
  let isSearchDropdownOpen = false;

  let users = [];
  let selectedUser = null;

  // Trading engine client state
  let clientState = "RUNNING"; // RUNNING, PAUSED, STOPPED
  let multiplier = 1.0;
  let masterOptIn = true;
  let strategies = {
    S1: true,
    S2: true,
    S3: true,
    S4: true,
    DOWN_AVGING: true,
    REV: false,
    CNC: true,
  };
  let positions = [];
  let totalPnl = 0;
  let openPositionsCount = 0;
  let filteredUsers = [];
  let timer;
  let isLoading = false;
  let errorMessage = null;

  // Authentic F1F2 Client Sizing State
  let tier = null; // "H", "M", "L", "CUSTOM", or null
  let customLot = 25;
  let amount = 500000;
  let dwnAvgQty = 1;
  let isSavingConfig = false;

  // Preview Scrips State
  let previewScrips = [];
  let isLoadingPreviewScrips = false;

  // Sandbox Virtual Testing State
  let virtualBalance = 1000000.0;
  let showVirtualBalanceModal = false;

  // Tag Edit Modal State
  let showEditTagModal = false;
  let newTagInput = "";
  let isSavingTag = false;

  // Reactive Route Environment Watcher (handles browser Back/Forward navigation)
  $: if (f7route && f7route.query && f7route.query.env) {
    const routeEnv = f7route.query.env.toUpperCase();
    if (routeEnv !== currentEnv) {
      applyEnvChange(routeEnv);
    }
  }

  function handleEnvSwitch(newEnv) {
    if (currentEnv === newEnv) return;

    if (newEnv === "LIVE") {
      if (!canAccessLive) {
        f7.dialog.alert("Access Restricted: Your role is not authorized for Live Production Trading.", "Restricted");
        return;
      }
      f7.dialog.confirm(
        "⚠️ You are switching to LIVE Production mode. Any changes saved will directly affect live broker execution and real capital. Do you wish to continue?",
        "Switch to LIVE Production",
        () => {
          applyEnvChange(newEnv);
        }
      );
      return;
    }

    applyEnvChange(newEnv);
  }

  function applyEnvChange(newEnv) {
    if (activeAbortController) {
      activeAbortController.abort();
    }
    activeAbortController = new AbortController();
    if (timer) clearInterval(timer);

    currentEnv = newEnv;
    requestGeneration++;
    sessionStorage.setItem("dealer_terminal_env", newEnv);

    // Reset selection if mismatched
    if (selectedUser) {
      const isSimUser = selectedUser.ucc?.startsWith("SIM_") || selectedUser.is_simulation;
      if ((newEnv === "SIMULATION" && !isSimUser) || (newEnv === "LIVE" && isSimUser)) {
        selectedUser = null;
        ucc = "";
        searchInput = "";
      }
    }

    positions = [];
    errorMessage = null;

    // Update URL query cleanly
    const targetUcc = selectedUser ? (selectedUser.client_tag || selectedUser.ucc) : (ucc || "");
    f7router.navigate(`/user-trading-controls/?${targetUcc ? 'ucc=' + encodeURIComponent(targetUcc) + '&' : ''}env=${newEnv}`, {
      replaceState: true,
    });

    fetchDbUsers(requestGeneration, activeAbortController.signal);
    if (ucc) {
      fetchClientConfig(requestGeneration, activeAbortController.signal);
      loadPreviewScrips();
      fetchClientData(requestGeneration, activeAbortController.signal);
      timer = setInterval(() => fetchClientData(requestGeneration), 3000);
    }
  }

  $: totalPnl = positions.reduce((acc, p) => acc + (p.unrealized_pnl || p.pnl || 0), 0);
  $: openPositionsCount = positions.filter((p) => p.status === "OPEN" || (p.net_qty || p.filled_qty || p.ordered_qty || 0) !== 0).length;

  // Filter users by search query
  $: filteredUsers = users.filter((u) => {
    if (!searchInput.trim()) return true;
    const q = searchInput.toLowerCase();
    return (
      (u.fullName && u.fullName.toLowerCase().includes(q)) ||
      (u.mobile && u.mobile.includes(q)) ||
      (u.pan && u.pan.toLowerCase().includes(q)) ||
      (u.ucc && u.ucc.toLowerCase().includes(q)) ||
      (u.client_tag && u.client_tag.toLowerCase().includes(q))
    );
  }).slice(0, 15);

  // Fetch Database Users scoped by active environment
  const fetchDbUsers = async (gen = requestGeneration, signal = null) => {
    try {
      const { users: fetchedUsers } = await fetchUsersDirectory(currentEnv, false);
      if (gen !== requestGeneration) return;
      users = fetchedUsers;
      resolveSelectedUser();
    } catch (e) {
      console.error("Failed to load users:", e);
    }
  };


  function resolveSelectedUser() {
    if (!ucc) {
      selectedUser = null;
      return;
    }
    const target = ucc.trim().toUpperCase();
    const found = users.find(
      (u) =>
        (u.client_tag && u.client_tag.toUpperCase() === target) ||
        (u.ucc && u.ucc.toUpperCase() === target) ||
        u.mobile === target ||
        u.user_id === target
    );
    if (found) {
      selectedUser = found;
      searchInput = `${found.fullName} (${found.client_tag || found.ucc || found.mobile})`;
    } else {
      selectedUser = null;
      searchInput = ucc;
    }
  }

  function selectUser(u) {
    selectedUser = u;
    ucc = (u.client_tag || u.ucc || "").toUpperCase();
    searchInput = `${u.fullName} ${ucc ? `(${ucc})` : `(${u.mobile})`}`;
    isSearchDropdownOpen = false;
    positions = [];
    if (timer) clearInterval(timer);
    if (ucc) {
      fetchClientConfig();
      loadPreviewScrips();
      fetchClientData();
      timer = setInterval(fetchClientData, 3000);
    }
  }

  async function loadPreviewScrips() {
    if (previewScrips.length > 0) return;
    isLoadingPreviewScrips = true;
    try {
      const res = await TradingService.fetchCompanyArray({ limit: 6, trading_st: 1 });
      if (res && Array.isArray(res.data) && res.data.length > 0) {
        previewScrips = res.data;
      } else {
        throw new Error("No data returned");
      }
    } catch (e) {
      previewScrips = [
        { Stock_Name: "RELIANCE", Security_Code: 500325, H_qty: 5, M_qty: 2, L_qty: 1 },
        { Stock_Name: "SBIN", Security_Code: 500112, H_qty: 50, M_qty: 20, L_qty: 10 },
        { Stock_Name: "TCS", Security_Code: 532540, H_qty: 5, M_qty: 2, L_qty: 1 },
        { Stock_Name: "INFY", Security_Code: 500209, H_qty: 10, M_qty: 5, L_qty: 2 },
        { Stock_Name: "WIPRO", Security_Code: 507685, H_qty: 50, M_qty: 20, L_qty: 10 },
      ];
    } finally {
      isLoadingPreviewScrips = false;
    }
  }

  function getCalculatedQty(scrip, t, m, cLot) {
    const mult = Number(m) || 1.0;
    if (t === "CUSTOM") {
      return Math.round((Number(cLot) || 1) * mult);
    }
    if (t === "H") return Math.round((Number(scrip.H_qty) || 0) * mult);
    if (t === "M") return Math.round((Number(scrip.M_qty) || 0) * mult);
    if (t === "L") return Math.round((Number(scrip.L_qty) || 0) * mult);
    return 0;
  }

  const fetchClientConfig = async (gen = requestGeneration, signal = null) => {
    if (!ucc) return;
    try {
      const data = await TradingService.fetchClientConfig(ucc, currentEnv === "SIMULATION");
      if (gen !== requestGeneration) return;

      if (data.multiplier !== undefined && data.multiplier > 0) multiplier = Number(data.multiplier);
      if (data.tier !== undefined) {
        const rawTier = data.tier ? String(data.tier).toUpperCase().trim() : null;
        tier = rawTier === "NULL" || rawTier === "UNASSIGNED" ? null : rawTier;
      } else if (selectedUser && selectedUser.tier) {
        const rawTier = String(selectedUser.tier).toUpperCase().trim();
        tier = rawTier === "NULL" || rawTier === "UNASSIGNED" ? null : rawTier;
      }

      if (data.custom_lot !== undefined && data.custom_lot > 0) customLot = Number(data.custom_lot);
      else if (data.customLot !== undefined && data.customLot > 0) customLot = Number(data.customLot);
      else if (selectedUser && selectedUser.custom_lot) customLot = Number(selectedUser.custom_lot);

      if (data.amount !== undefined && data.amount > 0) amount = Number(data.amount);
      else if (data.investment_capital !== undefined && data.investment_capital > 0) amount = Number(data.investment_capital);
      else if (selectedUser && (selectedUser.amount || selectedUser.investment_capital)) amount = Number(selectedUser.amount || selectedUser.investment_capital);

      if (data.dwn_avg_qty !== undefined) dwnAvgQty = Number(data.dwn_avg_qty);
      else if (data.dwnAvgQty !== undefined) dwnAvgQty = Number(data.dwnAvgQty);
      else if (data.dwnAvg_qty !== undefined) dwnAvgQty = Number(data.dwnAvg_qty);
      else if (selectedUser && (selectedUser.dwn_avg_qty || selectedUser.dwnAvg_qty)) dwnAvgQty = Number(selectedUser.dwn_avg_qty || selectedUser.dwnAvg_qty);

      if (data.state) clientState = data.state;
      if (data.virtual_balance > 0) virtualBalance = data.virtual_balance;
      if (data.master_opt_in !== undefined) masterOptIn = Boolean(data.master_opt_in);
      else if (selectedUser && selectedUser.master_opt_in !== undefined) masterOptIn = Boolean(selectedUser.master_opt_in);

      if (data.strategies) {
        strategies = {
          S1: Boolean(data.strategies.S1),
          S2: Boolean(data.strategies.S2),
          S3: data.strategies.S3 !== undefined ? Boolean(data.strategies.S3) : true,
          S4: data.strategies.S4 !== undefined ? Boolean(data.strategies.S4) : true,
          DOWN_AVGING: data.strategies.DOWN_AVGING !== undefined ? Boolean(data.strategies.DOWN_AVGING) : true,
          REV: Boolean(data.strategies.REV),
          CNC: Boolean(data.strategies.CNC),
        };
      }
    } catch (e) {
      if (e.name === "AbortError") return;
      console.warn("Could not load client config:", e);
      if (selectedUser) {
        if (selectedUser.master_opt_in !== undefined) masterOptIn = Boolean(selectedUser.master_opt_in);
        if (selectedUser.tier) {
          const rawTier = String(selectedUser.tier).toUpperCase().trim();
          tier = rawTier === "NULL" || rawTier === "UNASSIGNED" ? null : rawTier;
        }
        if (selectedUser.multiplier) multiplier = Number(selectedUser.multiplier);
        if (selectedUser.custom_lot) customLot = Number(selectedUser.custom_lot);
        if (selectedUser.amount) amount = Number(selectedUser.amount);
      }
    }
  };

  const fetchClientData = async (gen = requestGeneration, signal = null) => {
    if (!ucc) return;
    try {
      const endpoint = currentEnv === "SIMULATION" ? `${ENGINE_URL}/sim/client/${ucc}/positions` : `${ENGINE_URL}/client/${ucc}/positions`;
      const res = await fetch(endpoint, { signal });
      if (res.ok) {
        if (gen !== requestGeneration) return;
        const data = await res.json();
        positions = Array.isArray(data) ? data : data && Array.isArray(data.positions) ? data.positions : [];
        errorMessage = null;
      } else {
        if (gen !== requestGeneration) return;
        positions = [];
        errorMessage = `Engine returned HTTP ${res.status}: ${res.statusText}`;
      }
    } catch (err) {
      if (err.name === "AbortError") return;
      console.error("Error fetching client positions from engine:", err);
      positions = [];
      errorMessage = err.message || "Failed to reach trading engine";
    }
  };

  onMount(() => {
    f7ready(async () => {
      const token = localStorage.getItem("token");
      if (!token) {
        if (f7router) {
          f7router.navigate("/");
        } else {
          window.location.href = "/";
        }
        return;
      }
      await fetchDbUsers();
      if (ucc) {
        fetchClientConfig();
        loadPreviewScrips();
        fetchClientData();
        timer = setInterval(fetchClientData, 3000);
      }
    });
  });

  onDestroy(() => {
    if (timer) clearInterval(timer);
  });

  async function executeClientAction(action) {
    if (!ucc) return;
    isLoading = true;
    try {
      const endpoint = currentEnv === "SIMULATION" ? `${ENGINE_URL}/sim/client/${ucc}/${action}` : `${ENGINE_URL}/client/${ucc}/${action}`;
      const res = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });
      if (!res.ok) {
        throw new Error(`Engine returned HTTP ${res.status}`);
      }
      const data = await res.json();
      if (action === "start") clientState = "RUNNING";
      if (action === "pause") clientState = "PAUSED";
      if (action === "stop") clientState = "STOPPED";
      if (action === "squareoff") {
        clientState = "STOPPED";
        positions = [];
        SoundAlertManager.playChime("KILLSWITCH");
        f7.dialog.alert(
          `Emergency Square-Off executed for UCC Tag: ${ucc} (${currentEnv}). ${data.positions_closed || 0} positions closed.`,
          "SQUARED OFF"
        );
      } else {
        f7.toast.create({ text: `Client ${ucc} state set to ${clientState} (${currentEnv})`, closeTimeout: 2000 }).open();
      }
    } catch (err) {
      console.error(`Failed to execute ${action} for client:`, err);
      f7.toast.create({ text: `Action ${action} failed: ${err.message}`, closeTimeout: 3000 }).open();
    } finally {
      isLoading = false;
    }
  }

  async function updateMultiplier(val) {
    if (!ucc) return;
    multiplier = Math.round(Number(val) * 10) / 10;
    try {
      await TradingService.saveClientConfig(ucc, { multiplier }, currentEnv === "SIMULATION");
      f7.toast.create({ text: `Multiplier updated to ${multiplier.toFixed(1)}x (${currentEnv})`, closeTimeout: 1500 }).open();
    } catch (e) {
      console.error("Multiplier engine update failed:", e);
      f7.toast.create({ text: `Failed to update multiplier: ${e.message}`, closeTimeout: 3000 }).open();
    }
  }

  async function updateSubscriptions() {
    if (!ucc) return;
    try {
      const activeStrats = Object.entries(strategies || {})
        .filter(([_, active]) => Boolean(active))
        .map(([stratKey]) => stratKey);

      await TradingService.toggleAutoTrade(
        ucc,
        masterOptIn,
        activeStrats,
        multiplier || 1.0,
        currentEnv === "SIMULATION"
      );
      f7.toast.create({ text: `Subscriptions updated for ${ucc} (${currentEnv})`, closeTimeout: 1500 }).open();
    } catch (e) {
      console.error("Subscriptions engine update failed:", e);
      f7.toast.create({ text: `Failed to update subscriptions: ${e.message}`, closeTimeout: 3000 }).open();
    }
  }

  function handleMasterOptInToggle() {
    const nextVal = !masterOptIn;
    if (nextVal && isTierUnassigned(tier)) {
      f7.dialog.confirm(
        "⚠️ Client tier is UNASSIGNED. The trading engine will not place orders for unassigned clients (NON-OP state). Are you sure you want to enable auto-trade?",
        "Unassigned Tier Warning",
        () => {
          masterOptIn = true;
          updateSubscriptions();
        }
      );
    } else {
      masterOptIn = nextVal;
      updateSubscriptions();
    }
  }

  async function saveInvestmentConfig() {
    if (!ucc) return;
    isSavingConfig = true;
    try {
      const payload = {
        tier: tier || null,
        custom_lot: tier === "CUSTOM" ? Number(customLot) || 1 : 0,
        amount: Number(amount) || 0,
        multiplier: Number(multiplier) || 1.0,
        dwn_avg_qty: Number(dwnAvgQty) || 1,
        master_opt_in: Boolean(masterOptIn),
        strategies: {
          S1: Boolean(strategies.S1),
          S2: Boolean(strategies.S2),
          S3: Boolean(strategies.S3),
          S4: Boolean(strategies.S4),
          DOWN_AVGING: Boolean(strategies.DOWN_AVGING),
          REV: Boolean(strategies.REV),
          CNC: Boolean(strategies.CNC),
        },
      };

      await TradingService.saveClientConfig(ucc, payload, currentEnv === "SIMULATION");

      if (selectedUser) {
        selectedUser = {
          ...selectedUser,
          tier: payload.tier,
          custom_lot: payload.custom_lot,
          amount: payload.amount,
          multiplier: payload.multiplier,
          dwn_avg_qty: payload.dwn_avg_qty,
          master_opt_in: payload.master_opt_in,
        };
      }

      f7.toast.create({ text: `✓ Configuration saved for ${ucc} (${currentEnv})`, closeTimeout: 2000 }).open();
    } catch (e) {
      console.error("Failed to save investment config:", e);
      f7.dialog.alert(e.message || "Failed to save configuration", "Save Error");
    } finally {
      isSavingConfig = false;
    }
  }

  function handleManualSearch() {
    isSearchDropdownOpen = false;
    if (searchInput.trim()) {
      const clean = searchInput.trim().toUpperCase();
      const match = users.find(
        (u) =>
          (u.client_tag && u.client_tag.toUpperCase() === clean) ||
          (u.ucc && u.ucc.toUpperCase() === clean) ||
          u.mobile === clean
      );
      if (match) {
        selectUser(match);
      } else {
        ucc = clean;
        selectedUser = null;
        positions = [];
        if (timer) clearInterval(timer);
        if (ucc) {
          fetchClientData();
          timer = setInterval(fetchClientData, 3000);
        }
      }
    }
  }

  function openEditTagModal() {
    showEditTagModal = true;
  }

  async function handleTagAssignedSuccess(e) {
    const { user: updatedUser, tag, multiplier: mult } = e.detail;
    ucc = tag;
    multiplier = mult;
    if (selectedUser) {
      selectedUser.client_tag = tag;
      selectedUser.ucc = tag;
      searchInput = `${selectedUser.fullName} (${tag})`;
    }
    await fetchDbUsers();
    if (timer) clearInterval(timer);
    fetchClientData();
    timer = setInterval(fetchClientData, 3000);
  }
</script>

<Page name="user-trading-controls">
  <TradingNavbar title="Client Trading & Strategy Controls">
    <div style="display: flex; align-items: center; gap: 8px; margin-right: 8px;">
      <EnvironmentSwitcher
        environment={currentEnv}
      />
    </div>
    {#if ucc}
      <Button fill color="red" small onClick={() => executeClientAction('squareoff')}>
        <Icon f7="exclamationmark_octagon" size="16" /> &nbsp; SQUARE-OFF CLIENT
      </Button>
    {/if}
  </TradingNavbar>

  <Block>
    <!-- Multi-User Search & Autocomplete Header -->
    <Card class="search-card">
      <CardContent>
        <label class="form-label" for="userSearchBox">Look up User or UCC Tag (Production Database & Engine):</label>
        <UserSearchTypeahead
          id="userSearchBox"
          {users}
          placeholder={currentEnv === 'SIMULATION' ? 'Search simulation accounts by Name, UCC (e.g. SIM_1001)...' : 'Search by Full Name, Mobile Number, PAN, or UCC Tag...'}
          bind:value={searchInput}
          on:select={(e) => selectUser(e.detail)}
          on:enter={handleManualSearch}
        />
      </CardContent>
    </Card>

    {#if !selectedUser && !ucc}
      <!-- NO USER SELECTED PLACEHOLDER SCREEN -->
      <Card class="no-user-card margin-top">
        <CardContent style="text-align: center; padding: 36px 20px;">
          <Icon f7="person_crop_circle_badge_exclamation" size="48" color="blue" />
          <h3 style="margin: 12px 0 6px 0; font-size: 18px; color: #0f172a;">No Client Account Selected</h3>
          <p style="color: #64748b; font-size: 14px; max-width: 500px; margin: 0 auto 20px auto;">
            Search and select a user from MongoDB to configure their live trading state, strategy subscriptions, risk limits, and view their live positions.
          </p>

          {#if users.length > 0}
            <div style="max-width: 700px; margin: 0 auto; text-align: left;">
              <div style="font-size: 13px; font-weight: 700; color: #334155; margin-bottom: 10px; text-transform: uppercase;">
                Registered Database Accounts ({users.length}):
              </div>
              <div class="quick-user-grid">
                {#each users.slice(0, 8) as u}
                  <button type="button" class="quick-user-pill" on:click={() => selectUser(u)}>
                    <div class="avatar-circle-small">
                      {(u.fullName || 'U').charAt(0).toUpperCase()}
                    </div>
                    <div style="flex: 1; min-width: 0;">
                      <div style="font-weight: 600; font-size: 13px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">
                        {u.fullName}
                      </div>
                      <div style="font-size: 11px; color: #64748b;">
                        {u.client_tag || u.ucc || u.mobile}
                      </div>
                    </div>
                    <span class="badge {u.client_tag || u.ucc ? 'color-green' : 'color-orange'}" style="font-size: 10px;">
                      {u.client_tag || u.ucc ? 'Tagged' : 'No Tag'}
                    </span>
                  </button>
                {/each}
              </div>
            </div>
          {/if}
        </CardContent>
      </Card>
    {:else}
      <!-- ELABORATED USER PROFILE CARD -->
      <Card class="profile-card">
        <CardContent>
          <div style="display: flex; justify-content: space-between; align-items: flex-start; flex-wrap: wrap; gap: 16px;">
            <!-- Left: User Identity -->
            <div style="display: flex; gap: 16px; align-items: center;">
              <div class="large-avatar">
                {#if selectedUser}
                  {(selectedUser.fullName || 'U').charAt(0).toUpperCase()}
                {:else}
                  ?
                {/if}
              </div>
              <div>
                <div style="display: flex; align-items: center; gap: 8px;">
                  <h2 style="margin: 0; font-size: 20px; color: #0f172a;">
                    {selectedUser ? selectedUser.fullName : 'Direct Tag Execution'}
                  </h2>
                  {#if selectedUser}
                    <Badge color={selectedUser.account_status === 'approved' ? 'green' : 'orange'}>
                      {selectedUser.account_status || 'Approved'}
                    </Badge>
                  {/if}
                </div>
                <div style="display: flex; gap: 16px; margin-top: 6px; font-size: 13px; color: #475569; flex-wrap: wrap;">
                  {#if selectedUser}
                    <span>📱 <strong>{selectedUser.mobile || '-'}</strong></span>
                    <span>💳 PAN: <strong>{selectedUser.pan || '-'}</strong></span>
                    <span>✉️ {selectedUser.emailAddress || '-'}</span>
                  {:else}
                    <span style="color: #ea580c; font-style: italic;">
                      Direct execution on UCC Tag: {ucc}
                    </span>
                  {/if}
                </div>
              </div>
            </div>

            <!-- Right: Active UCC Tag & Edit Action -->
            <div class="tag-box">
              <div style="font-size: 11px; text-transform: uppercase; color: #64748b; font-weight: bold;">
                Trading UCC Tag &amp; Tier
              </div>
              <div style="display: flex; align-items: center; gap: 8px; margin-top: 4px;">
                {#if ucc}
                  <span class="active-tag-badge">{ucc}</span>
                  {#if isTierUnassigned(tier)}
                    <span class="badge color-red" style="font-size: 11px; padding: 4px 8px; font-weight: 700;">
                      ⚠️ UNASSIGNED
                    </span>
                  {:else}
                    <span class="badge {tier === 'H' ? 'color-red' : tier === 'M' ? 'color-orange' : tier === 'L' ? 'color-green' : 'color-purple'}" style="font-size: 11px; padding: 4px 8px; font-weight: 700;">
                      {formatTierBadge(tier, customLot)}
                    </span>
                  {/if}
                  <Button small outline color="blue" onClick={openEditTagModal}>
                    Edit Tag
                  </Button>
                {:else}
                  <span class="badge color-orange" style="padding: 6px 10px; font-weight: bold;">
                    No UCC Assigned
                  </span>
                  <Button small fill color="blue" onClick={openEditTagModal}>
                    Assign UCC
                  </Button>
                {/if}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {#if !ucc}
        <Card class="margin-top" style="background: #fffbeb; border: 1px solid #fef3c7;">
          <CardContent style="display: flex; align-items: center; justify-content: space-between; gap: 12px;">
            <div style="display: flex; align-items: center; gap: 12px;">
              <Icon f7="exclamationmark_triangle_fill" color="orange" size="24" />
              <div>
                <strong style="color: #92400e;">UCC Tag Required for Live Trading</strong>
                <div style="font-size: 13px; color: #b45309;">
                  Please assign a valid UCC Tag to <strong>{selectedUser ? selectedUser.fullName : 'this user'}</strong> to enable the execution engine, risk gate, and live position streaming.
                </div>
              </div>
            </div>
            <Button fill color="orange" onClick={openEditTagModal}>
              Assign UCC Now
            </Button>
          </CardContent>
        </Card>
      {:else}
        <!-- TRADING & STRATEGY EXECUTION CONTROLS -->
        <Row>
          <!-- State & Multiplier -->
          <Col width="100" medium="40">
            <Card>
              <CardHeader class="display-flex justify-content-space-between align-items-center">
                <div>
                  <strong>Execution Engine State</strong>
                  <div class="text-color-gray font-size-12">Trading Engine Status</div>
                </div>
                <span
                  class="badge {clientState === 'RUNNING' ? 'color-green' : clientState === 'PAUSED' ? 'color-orange' : 'color-red'}"
                  style="font-size: 13px; padding: 6px 12px; font-weight: 700;"
                >
                  {clientState}
                </span>
              </CardHeader>
          <CardContent>
            <div class="display-flex justify-content-space-between margin-bottom">
              <Button
                fill
                color="green"
                small
                disabled={clientState === 'RUNNING' || isLoading}
                onClick={() => executeClientAction('start')}
              >
                START / RESUME
              </Button>
              <Button
                fill
                color="orange"
                small
                disabled={clientState === 'PAUSED' || isLoading}
                onClick={() => executeClientAction('pause')}
              >
                PAUSE (HOLD SL)
              </Button>
              <Button
                fill
                color="gray"
                small
                disabled={clientState === 'STOPPED' || isLoading}
                onClick={() => executeClientAction('stop')}
              >
                STOP
              </Button>
            </div>

            <div class="margin-top" style="border-top: 1px solid #f1f5f9; padding-top: 12px;">
              <div class="display-flex justify-content-space-between align-items-center">
                <span style="font-weight: 600;">Risk Sizing Multiplier:</span>
                <strong style="font-size: 16px; color: #2563eb;">{multiplier.toFixed(1)}x</strong>
              </div>
              <Range
                min={0.1}
                max={10.0}
                step={0.1}
                value={multiplier}
                onRangeChange={updateMultiplier}
              />
              <div style="display: flex; gap: 6px; margin-top: 8px;">
                {#each [0.5, 1.0, 2.0, 5.0] as preset}
                  <Button
                    small
                    outline
                    class={multiplier === preset ? 'button-active' : ''}
                    onClick={() => updateMultiplier(preset)}
                    style="flex: 1;"
                  >
                    {preset}x
                  </Button>
                {/each}
              </div>
            </div>
          </CardContent>
        </Card>
      </Col>

      <!-- Strategy Allocation Matrix (7 Strategies) -->
      <Col width="100" medium="60">
        <Card>
          <CardHeader class="display-flex justify-content-space-between align-items-center">
            <div>
              <strong>Strategy Allocation Matrix (7 Strategies)</strong>
              <div class="text-color-gray font-size-12">Authentic F1F2 Execution Matrix</div>
            </div>
            <Button small outline color="blue" onClick={updateSubscriptions}>
              Sync Subscriptions
            </Button>
          </CardHeader>
          <CardContent>
            <Row>
              <Col width="50">
                <div class="strategy-row">
                  <div>
                    <div style="font-weight: 600; color: #0f172a;">S1 Standard Intraday</div>
                    <div style="font-size: 11px; color: #64748b;">Primary momentum trigger</div>
                  </div>
                  <Toggle
                    checked={strategies.S1}
                    onToggleChange={() => {
                      strategies.S1 = !strategies.S1;
                      updateSubscriptions();
                    }}
                  />
                </div>
                <div class="strategy-row">
                  <div>
                    <div style="font-weight: 600; color: #0f172a;">S2 Breakout Momentum</div>
                    <div style="font-size: 11px; color: #64748b;">High Beta Equities</div>
                  </div>
                  <Toggle
                    checked={strategies.S2}
                    onToggleChange={() => {
                      strategies.S2 = !strategies.S2;
                      updateSubscriptions();
                    }}
                  />
                </div>
                <div class="strategy-row">
                  <div>
                    <div style="font-weight: 600; color: #0f172a;">S3 Intraday Dip Long</div>
                    <div style="font-size: 11px; color: #64748b;">F1F2 Core Pullback Strategy</div>
                  </div>
                  <Toggle
                    checked={strategies.S3}
                    onToggleChange={() => {
                      strategies.S3 = !strategies.S3;
                      updateSubscriptions();
                    }}
                  />
                </div>
                <div class="strategy-row">
                  <div>
                    <div style="font-weight: 600; color: #0f172a;">S4 Short-Term Intraday</div>
                    <div style="font-size: 11px; color: #64748b;">F1F2 Fast Trend Strategy</div>
                  </div>
                  <Toggle
                    checked={strategies.S4}
                    onToggleChange={() => {
                      strategies.S4 = !strategies.S4;
                      updateSubscriptions();
                    }}
                  />
                </div>
              </Col>
              <Col width="50">
                <div class="strategy-row">
                  <div>
                    <div style="font-weight: 600; color: #0f172a;">Down-Averaging (Tranches)</div>
                    <div style="font-size: 11px; color: #64748b;">Tier lot scale on pullbacks</div>
                  </div>
                  <Toggle
                    checked={strategies.DOWN_AVGING}
                    onToggleChange={() => {
                      strategies.DOWN_AVGING = !strategies.DOWN_AVGING;
                      updateSubscriptions();
                    }}
                  />
                </div>
                <div class="strategy-row">
                  <div>
                    <div style="font-weight: 600; color: #0f172a;">Reverse Intraday Short</div>
                    <div style="font-size: 11px; color: #64748b;">Mean Reversion MIS</div>
                  </div>
                  <Toggle
                    checked={strategies.REV}
                    onToggleChange={() => {
                      strategies.REV = !strategies.REV;
                      updateSubscriptions();
                    }}
                  />
                </div>
                <div class="strategy-row">
                  <div>
                    <div style="font-weight: 600; color: #0f172a;">CNC Delivery Holding</div>
                    <div style="font-size: 11px; color: #64748b;">100% Cash Margin Delivery</div>
                  </div>
                  <Toggle
                    checked={strategies.CNC}
                    onToggleChange={() => {
                      strategies.CNC = !strategies.CNC;
                      updateSubscriptions();
                    }}
                  />
                </div>
              </Col>
            </Row>
          </CardContent>
        </Card>
      </Col>
    </Row>

    <!-- AUTHENTIC F1F2 TIER & SIZING ROW -->
    <Row class="margin-top">
      <!-- F1F2 Client Tier & Sizing Card -->
      <Col width="100" medium="50">
        <Card class="investment-sizing-card">
          <CardHeader class="display-flex justify-content-space-between align-items-center">
            <div>
              <strong style="color: #0f172a; font-size: 15px;">🏷️ F1F2 Client Tier &amp; Sizing</strong>
              <div class="text-color-gray font-size-12">Authentic F1F2 Base Lot &amp; Multiplier Controls</div>
            </div>
            <Button small fill color="blue" disabled={isSavingConfig} onClick={saveInvestmentConfig}>
              {isSavingConfig ? "Saving..." : "Save Config"}
            </Button>
          </CardHeader>
          <CardContent>
            {#if isTierUnassigned(tier)}
              <div class="tier-warning-banner">
                ⚠️ <strong>TIER UNASSIGNED:</strong> Client cannot execute orders until a Tier is assigned by Admin (NON-OP state).
              </div>
            {/if}

            <div class="item-input-wrap margin-bottom-half">
              <label class="control-sublabel" for="tierSelect">Client Sizing Tier (Compulsory):</label>
              <div class="tier-segmented-grid margin-top-xs">
                <button
                  type="button"
                  class={`tier-btn btn-tier-h ${tier === 'H' ? 'active' : ''}`}
                  on:click={() => (tier = 'H')}
                >
                  🔴 Tier H (High)
                </button>
                <button
                  type="button"
                  class={`tier-btn btn-tier-m ${tier === 'M' ? 'active' : ''}`}
                  on:click={() => (tier = 'M')}
                >
                  🟡 Tier M (Medium)
                </button>
                <button
                  type="button"
                  class={`tier-btn btn-tier-l ${tier === 'L' ? 'active' : ''}`}
                  on:click={() => (tier = 'L')}
                >
                  🟢 Tier L (Low)
                </button>
                <button
                  type="button"
                  class={`tier-btn btn-tier-custom ${tier === 'CUSTOM' ? 'active' : ''}`}
                  on:click={() => (tier = 'CUSTOM')}
                >
                  ⚪ Custom Lot
                </button>
              </div>
            </div>

            {#if tier === 'CUSTOM'}
              <div class="custom-lot-input-row margin-bottom-half">
                <label for="customLotInput" class="control-sublabel">Custom Base Lot (Shares):</label>
                <input
                  id="customLotInput"
                  type="number"
                  min="1"
                  step="1"
                  class="custom-field-input tabular-mono"
                  placeholder="e.g. 25"
                  bind:value={customLot}
                />
                {#if !customLot || Number(customLot) <= 0}
                  <span class="field-error-text">⚠️ Custom lot size must be at least 1 share</span>
                {/if}
              </div>
            {/if}

            <div class="item-input-wrap margin-bottom-half">
              <div class="display-flex justify-content-space-between align-items-center">
                <label class="control-sublabel" for="multiplierStepper">Multiplier Factor:</label>
                <strong style="color: #2563eb; font-size: 14px;">{Number(multiplier || 1).toFixed(1)}x</strong>
              </div>
              <div style="display: flex; gap: 6px; margin-top: 6px; margin-bottom: 8px;">
                {#each [0.5, 1.0, 1.5, 2.0, 3.0, 5.0] as preset}
                  <Button
                    small
                    outline
                    class={multiplier === preset ? 'button-active' : ''}
                    onClick={() => (multiplier = preset)}
                    style="flex: 1;"
                  >
                    {preset.toFixed(1)}x
                  </Button>
                {/each}
              </div>
              <div class="mult-input-group">
                <button
                  type="button"
                  class="stepper-btn"
                  on:click={() => (multiplier = Math.max(0.1, Math.round(((multiplier || 1) - 0.5) * 10) / 10))}
                >
                  -
                </button>
                <input
                  id="multiplierStepper"
                  type="number"
                  step="0.1"
                  min="0.1"
                  max="10.0"
                  class="stepper-input tabular-mono"
                  bind:value={multiplier}
                />
                <button
                  type="button"
                  class="stepper-btn"
                  on:click={() => (multiplier = Math.min(10.0, Math.round(((multiplier || 1) + 0.5) * 10) / 10))}
                >
                  +
                </button>
              </div>
            </div>

            <div class="item-input-wrap margin-bottom-half">
              <label class="control-sublabel" for="dwnAvgSelect">Down-Avg Factor (dwnAvg_qty):</label>
              <select id="dwnAvgSelect" class="custom-field-select" bind:value={dwnAvgQty}>
                <option value={1}>1x (Same as Base Tranche)</option>
                <option value={2}>2x (Double Lot on Dips)</option>
              </select>
              <div class="font-size-11 text-color-gray margin-top-xs">
                {dwnAvgQty}x base shares dispatched on each pullback tranche
              </div>
            </div>

            <div class="item-input-wrap margin-bottom-half">
              <label class="control-sublabel" for="capitalAmountInput">Designated Capital Amount (₹):</label>
              <div class="currency-input-wrap">
                <span class="currency-prefix">₹</span>
                <input
                  id="capitalAmountInput"
                  type="number"
                  step="10000"
                  bind:value={amount}
                  placeholder="e.g. 500000"
                  class="custom-field-input"
                />
              </div>
              <div class="font-size-11 text-color-gray margin-top-xs">
                Formatted: <strong>{formatCapital(amount)}</strong>
              </div>
            </div>
          </CardContent>
        </Card>
      </Col>

      <!-- Live Stock Preview & Simulation Balance -->
      <Col width="100" medium="50">
        <Card class="sandbox-test-card">
          <CardHeader class="display-flex justify-content-space-between align-items-center">
            <div>
              <strong style="color: #0284c7; font-size: 15px;">📊 Live Stock Quantity Preview</strong>
              <div class="text-color-gray font-size-12">
                Calculated shares: Tier ({tier || 'None'}) × {Number(multiplier || 1).toFixed(1)}x
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {#if isLoadingPreviewScrips}
              <div style="padding: 24px; text-align: center; color: #94a3b8; font-size: 13px;">Loading scrip matrix...</div>
            {:else}
              <div class="preview-table-container">
                <table class="preview-table">
                  <thead>
                    <tr>
                      <th>Stock Symbol</th>
                      <th>BSE Code</th>
                      <th>Base Lot ({tier || '-'})</th>
                      <th style="text-align: right;">Effective Lot ({Number(multiplier || 1).toFixed(1)}x)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {#each previewScrips as scrip}
                      <tr>
                        <td>
                          <button
                            type="button"
                            class="stock-chart-btn"
                            on:click={() => openStockChart(scrip.Stock_Name, scrip.Security_Code)}
                            title="View Live Signal Chart"
                            style="background:none; border:none; cursor:pointer; color:#0284c7; font-weight:700; padding:0; display:inline-flex; align-items:center; gap:4px;"
                          >
                            <strong>{scrip.Stock_Name}</strong> <span style="font-size:11px;">📈</span>
                          </button>
                        </td>
                        <td class="tabular-mono" style="color: #64748b;">
                          {scrip.Security_Code || '-'}
                        </td>
                        <td class="tabular-mono">
                          {#if isTierUnassigned(tier)}
                            <span style="color: #ef4444; font-weight: 700;">-</span>
                          {:else if tier === 'CUSTOM'}
                            {customLot} shs
                          {:else if tier === 'H'}
                            {scrip.H_qty || 0} shs
                          {:else if tier === 'M'}
                            {scrip.M_qty || 0} shs
                          {:else if tier === 'L'}
                            {scrip.L_qty || 0} shs
                          {/if}
                        </td>
                        <td class="tabular-mono" style="text-align: right; font-weight: 800; color: #2563eb;">
                          {#if isTierUnassigned(tier)}
                            <span style="color: #ef4444;">⚠️ Unassigned</span>
                          {:else}
                            {getCalculatedQty(scrip, tier, multiplier, customLot)} shares
                          {/if}
                        </td>
                      </tr>
                    {/each}
                  </tbody>
                </table>
              </div>
            {/if}
          </CardContent>
        </Card>
      </Col>
    </Row>

    <!-- LIVE POSITIONS FOR THIS CLIENT -->
    <Card class="data-table margin-top">
      <CardHeader class="display-flex justify-content-space-between align-items-center">
        <div>
          <strong>Live Client Positions ({ucc})</strong>
          <span style="font-size: 12px; color: #64748b; margin-left: 8px;">
            Open Positions: <strong>{openPositionsCount}</strong>
          </span>
        </div>
        <div style="font-size: 16px; font-weight: bold; color: {(totalPnl || 0) >= 0 ? '#4cd964' : '#ff3b30'};">
          P&L: ₹{(Number(totalPnl) || 0).toFixed(2)}
        </div>
      </CardHeader>
      <CardContent padding={false}>
        <table>
          <thead>
            <tr>
              <th class="label-cell">Symbol</th>
              <th class="label-cell">Strategy</th>
              <th class="numeric-cell">Net Qty</th>
              <th class="numeric-cell">Avg Price</th>
              <th class="numeric-cell">LTP</th>
              <th class="numeric-cell">Unrealized P&L</th>
              <th class="label-cell">Status</th>
            </tr>
          </thead>
          <tbody>
            {#if positions.length === 0}
              <tr>
                <td colspan="7" style="text-align: center; padding: 28px; color: #94a3b8;">
                  No active open positions for UCC tag {ucc}
                </td>
              </tr>
            {:else}
              {#each positions as pos}
                <tr>
                  <td class="label-cell font-bold">{pos.symbol || "-"}</td>
                  <td class="label-cell">{pos.strategy_id || "S1"}</td>
                  <td class="numeric-cell">{pos.filled_qty || pos.ordered_qty || pos.net_qty || 0}</td>
                  <td class="numeric-cell">₹{(pos.avg_price || 0).toFixed(2)}</td>
                  <td class="numeric-cell">₹{(pos.ltp || 0).toFixed(2)}</td>
                  <td
                    class="numeric-cell"
                    style="color: {(pos.unrealized_pnl || 0) >= 0 ? '#4cd964' : '#ff3b30'}; font-weight: bold;"
                  >
                    ₹{(pos.unrealized_pnl || 0).toFixed(2)}
                  </td>
                  <td class="label-cell">
                    <span class="badge {pos.status === 'OPEN' ? 'color-green' : 'color-orange'}">
                      {pos.status || "OPEN"}
                    </span>
                  </td>
                </tr>
              {/each}
            {/if}
          </tbody>
        </table>
      </CardContent>
    </Card>
      <!-- Client Execution Audit History Timeline -->
      <div class="margin-top">
        <AuditClientTimeline
          {ucc}
          environment={currentEnv}
        />
      </div>
    {/if}
    {/if}
  </Block>

  <!-- POPUP: EDIT / ASSIGN UCC TAG -->
  <AssignUccTagModal
    bind:opened={showEditTagModal}
    user={selectedUser}
    {users}
    lockUser={true}
    initialTag={ucc}
    initialMultiplier={multiplier}
    environment={currentEnv}
    on:success={handleTagAssignedSuccess}
  />

  <!-- POPUP: VIRTUAL BALANCE MODAL -->
  <VirtualBalanceModal
    bind:opened={showVirtualBalanceModal}
    {ucc}
    currentBalance={virtualBalance}
    on:success={(e) => {
      if (e.detail && e.detail.virtual_balance) {
        virtualBalance = e.detail.virtual_balance;
        amount = e.detail.virtual_balance;
      }
    }}
  />
  <!-- POPUP: REAL-TIME SIGNAL CHART MODAL -->
  <LiveSignalChartModal
    isOpen={showChartModal}
    symbol={chartSymbol}
    scripCode={chartScripCode}
    {ucc}
    onClose={() => (showChartModal = false)}
  />
</Page>

<style>
  /* F1F2 Tier segmented buttons */
  .tier-segmented-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 8px;
    margin-bottom: 12px;
  }
  .tier-btn {
    border: 1px solid #cbd5e1;
    background: #f8fafc;
    color: #475569;
    font-size: 12px;
    font-weight: 700;
    padding: 8px 4px;
    border-radius: 6px;
    cursor: pointer;
    transition: all 0.15s ease;
    text-align: center;
  }
  .tier-btn:hover {
    border-color: #94a3b8;
    background: #f1f5f9;
  }
  .tier-btn.active.btn-tier-h {
    background: #fee2e2;
    color: #b91c1c;
    border-color: #ef4444;
    box-shadow: 0 0 8px rgba(239, 68, 68, 0.25);
  }
  .tier-btn.active.btn-tier-m {
    background: #fef3c7;
    color: #b45309;
    border-color: #f59e0b;
    box-shadow: 0 0 8px rgba(245, 158, 11, 0.25);
  }
  .tier-btn.active.btn-tier-l {
    background: #dcfce7;
    color: #15803d;
    border-color: #22c55e;
    box-shadow: 0 0 8px rgba(34, 197, 94, 0.25);
  }
  .tier-btn.active.btn-tier-custom {
    background: #e0e7ff;
    color: #4338ca;
    border-color: #6366f1;
    box-shadow: 0 0 8px rgba(99, 102, 241, 0.25);
  }
  .tier-warning-banner {
    background: rgba(239, 68, 68, 0.12);
    border: 1px solid #ef4444;
    color: #b91c1c;
    border-radius: 6px;
    padding: 8px 12px;
    font-size: 11px;
    margin-bottom: 12px;
    line-height: 1.4;
  }
  .custom-lot-input-row {
    margin-top: 6px;
    display: flex;
    flex-direction: column;
    gap: 4px;
    background: #f8fafc;
    padding: 10px 12px;
    border-radius: 6px;
    border: 1px dashed #cbd5e1;
  }
  .field-error-text {
    font-size: 11px;
    color: #ef4444;
    font-weight: 600;
  }
  .mult-input-group {
    display: flex;
    align-items: center;
    gap: 4px;
  }
  .stepper-btn {
    width: 28px;
    height: 28px;
    border: 1px solid #cbd5e1;
    background: #f1f5f9;
    color: #1e293b;
    border-radius: 4px;
    font-size: 14px;
    font-weight: 700;
    cursor: pointer;
  }
  .stepper-btn:hover {
    background: #e2e8f0;
  }
  .stepper-input {
    width: 60px;
    height: 28px;
    border: 1px solid #cbd5e1;
    background: #ffffff;
    color: #1e293b;
    text-align: center;
    border-radius: 4px;
    font-size: 12px;
    font-weight: 700;
  }
  .preview-table-container {
    overflow-x: auto;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    background: #ffffff;
  }
  .preview-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 12px;
    text-align: left;
  }
  .preview-table th {
    background: #f8fafc;
    color: #64748b;
    padding: 8px 10px;
    font-weight: 700;
    text-transform: uppercase;
    font-size: 10px;
    letter-spacing: 0.3px;
    border-bottom: 1px solid #e2e8f0;
    white-space: nowrap;
  }
  .preview-table td {
    padding: 8px 10px;
    border-bottom: 1px solid #f1f5f9;
    white-space: nowrap;
  }
  .form-label {
    display: block;
    font-size: 13px;
    font-weight: 600;
    color: #334155;
    margin-bottom: 6px;
  }
  .control-sublabel {
    display: block;
    font-size: 12px;
    font-weight: 600;
    color: #475569;
    margin-bottom: 4px;
  }
  .currency-input-wrap {
    position: relative;
    display: flex;
    align-items: center;
  }
  .currency-prefix {
    position: absolute;
    left: 10px;
    font-weight: 700;
    color: #64748b;
    font-size: 14px;
  }
  .custom-field-input {
    width: 100%;
    padding: 8px 12px 8px 24px;
    border: 1px solid #cbd5e1;
    border-radius: 6px;
    font-size: 14px;
    background: #ffffff;
    color: #0f172a;
    box-sizing: border-box;
  }
  .custom-field-select {
    width: 100%;
    padding: 8px 12px;
    border: 1px solid #cbd5e1;
    border-radius: 6px;
    font-size: 13px;
    background: #ffffff;
    color: #0f172a;
    box-sizing: border-box;
  }
  .custom-field-input-sm {
    width: 100%;
    padding: 6px 8px;
    border: 1px solid #cbd5e1;
    border-radius: 4px;
    font-size: 12px;
    background: #ffffff;
    box-sizing: border-box;
  }
  .custom-field-select-sm {
    width: 100%;
    padding: 6px 8px;
    border: 1px solid #cbd5e1;
    border-radius: 4px;
    font-size: 12px;
    background: #ffffff;
    box-sizing: border-box;
  }
  .sizing-param-box {
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    padding: 10px 12px;
  }
  .virtual-balance-bar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    background: #faf5ff;
    border: 1px solid #e9d5ff;
    padding: 10px 14px;
    border-radius: 8px;
  }
  .virtual-balance-val {
    font-size: 18px;
    font-weight: 800;
    color: #7c3aed;
  }
  .sizing-preview-box {
    background: #f1f5f9;
    border: 1px solid #cbd5e1;
    border-radius: 8px;
    padding: 12px;
  }
  .preview-inputs-grid {
    display: grid;
    grid-template-columns: 1fr 1fr 1.2fr;
    gap: 8px;
  }
  .preview-result-callout {
    background: #ffffff;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    padding: 10px 12px;
    margin-top: 8px;
  }
  .preview-lot-size {
    font-size: 18px;
    color: #2563eb;
    margin-top: 2px;
  }
  .large-avatar {
    width: 52px;
    height: 52px;
    border-radius: 50%;
    background: #2563eb;
    color: #ffffff;
    font-weight: bold;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 22px;
  }
  .tag-box {
    background: #ffffff;
    padding: 10px 16px;
    border-radius: 8px;
    border: 1px solid #e2e8f0;
    text-align: right;
  }
  .active-tag-badge {
    background: #dbeafe;
    color: #1d4ed8;
    font-weight: 800;
    font-size: 16px;
    padding: 4px 10px;
    border-radius: 6px;
    font-family: monospace;
  }
  .strategy-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 10px 0;
    border-bottom: 1px solid #f1f5f9;
  }
  .quick-user-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
    gap: 8px;
  }
  .quick-user-pill {
    display: flex;
    align-items: center;
    gap: 10px;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    padding: 8px 12px;
    cursor: pointer;
    text-align: left;
    transition: all 0.2s ease;
  }
  .quick-user-pill:hover {
    background: #eff6ff;
    border-color: #93c5fd;
    transform: translateY(-1px);
  }
  .avatar-circle-small {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background: #dbeafe;
    color: #1e40af;
    font-weight: bold;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 13px;
    flex-shrink: 0;
  }
</style>
